// Phase 9 R3 — Inventory picker popover for the Add/Edit Part dialog.
// Server-side ilike search across 7 fields. Active items only.
// Hidden when `inventoryEnabled` is off (gating is the caller's responsibility).
import { useEffect, useState } from 'react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Boxes, Loader2, X } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useInventorySearch } from '@/hooks/inventory/useInventorySearch';
import { useInventoryItemById } from '@/hooks/inventory/useInventoryItemById';
import { StockPill } from '@/components/inventory/StockPill';
import type { InventoryItem } from '@/hooks/inventory/useInventoryItems';

interface Props {
  value: string | null;
  onPick: (item: InventoryItem) => void;
  onClear: () => void;
  /** Block changes/clears (e.g. when reservation > 0). */
  locked?: boolean;
  lockedReason?: string;
}

export function InventoryPickerPopover({ value, onPick, onClear, locked, lockedReason }: Props) {
  const { activeShop } = useAuth();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [debounced, setDebounced] = useState('');
  const { data: linked } = useInventoryItemById(value);
  const { data: results = [], isFetching } = useInventorySearch(activeShop?.id, debounced, open);

  useEffect(() => {
    const t = window.setTimeout(() => setDebounced(query), 250);
    return () => window.clearTimeout(t);
  }, [query]);

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2">
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="justify-start gap-2 flex-1 min-w-0"
              disabled={locked}
              title={locked ? lockedReason : undefined}
            >
              <Boxes className="h-3.5 w-3.5 shrink-0" />
              <span className="truncate">
                {linked
                  ? `${linked.part_number} · ${linked.name}`
                  : 'Link to inventory…'}
              </span>
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-[420px] p-0" align="start">
            <div className="p-2 border-b">
              <Input
                autoFocus
                placeholder="Search part #, name, brand, supplier…"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                className="h-8"
              />
            </div>
            <div className="max-h-72 overflow-y-auto">
              {isFetching && (
                <div className="flex items-center gap-2 p-3 text-xs text-muted-foreground">
                  <Loader2 className="h-3 w-3 animate-spin" /> Searching…
                </div>
              )}
              {!isFetching && debounced.trim().length === 0 && (
                <div className="p-3 text-xs text-muted-foreground">
                  Type to search inventory.
                </div>
              )}
              {!isFetching && debounced.trim().length > 0 && results.length === 0 && (
                <div className="p-3 text-xs text-muted-foreground">No matches.</div>
              )}
              {results.map((item) => {
                const avail = item.quantity_available ?? 0;
                const onHand = item.quantity_on_hand ?? 0;
                const rp = item.reorder_point ?? 0;
                return (
                  <button
                    key={item.id}
                    type="button"
                    onClick={() => {
                      onPick(item);
                      setOpen(false);
                      setQuery('');
                      setDebounced('');
                    }}
                    className="w-full text-left p-2 hover:bg-accent border-b last:border-b-0"
                  >
                    <div className="flex items-center justify-between gap-2">
                      <span className="font-medium text-sm truncate">{item.part_number}</span>
                      <StockPill available={avail} reorderPoint={rp} />
                    </div>
                    <div className="text-xs text-muted-foreground truncate">
                      {item.name}
                    </div>
                    <div className="text-[11px] text-muted-foreground truncate mt-0.5">
                      {[item.brand, item.supplier, item.bin_location && `Bin ${item.bin_location}`]
                        .filter(Boolean)
                        .join(' · ') || '—'}
                    </div>
                    <div className="text-[11px] text-muted-foreground tabular-nums mt-0.5">
                      Avail {avail} / On-hand {onHand}
                    </div>
                  </button>
                );
              })}
            </div>
          </PopoverContent>
        </Popover>
        {value && (
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-8 w-8"
            onClick={onClear}
            disabled={locked}
            title={locked ? lockedReason : 'Clear inventory link'}
          >
            <X className="h-3.5 w-3.5" />
          </Button>
        )}
      </div>
    </div>
  );
}
